import { api } from './index';

export interface TenderEvaluationReport {
  id: string;
  tenderId: string;
  generatedById: string;
  generatedBy: {
    id: string;
    fullName: string;
    email: string;
  };
  generatedAt: string;
  checkedById?: string | null;
  checkedBy?: {
    id: string;
    fullName: string;
    email: string;
  } | null;
  checkedAt?: string | null;
  checkedStatus: 'PENDING' | 'APPROVED' | 'REJECTED';
  checkedComments?: string | null;
  approvedById?: string | null;
  approvedBy?: {
    id: string;
    fullName: string;
    email: string;
  } | null;
  approvedAt?: string | null;
  approvalStatus: 'PENDING' | 'APPROVED' | 'REJECTED';
  approvalComments?: string | null;
  reportData: {
    tender: {
      id: string;
      referenceNo: string;
      description: string;
      category: string;
      publishingDate?: string;
      submissionDeadline?: string;
      openingDate?: string;
      currency?: 'MVR' | 'USD';
    };
    evaluationCriteria: Array<{
      id: string;
      criteriaName: string;
      description?: string;
      criteriaType: 'FINANCIAL' | 'GENERAL';
      weightage: number;
      order: number;
    }>;
    bidSubmissions: Array<{
      id: string;
      companyName: string;
      totalPrice: number;
      evaluationNotes?: string | null;
      scores: Array<{
        criteriaId: string;
        criteriaName: string;
        score: number;
        notes?: string | null;
      }>;
    }>;
    generatedAt: string;
  };
  status: 'PENDING_CHECK' | 'PENDING_APPROVAL' | 'PENDING_BID_COMMITTEE' | 'APPROVED' | 'REJECTED';
  createdAt: string;
  updatedAt: string;
  bidCommitteeApprovals?: Array<{
    id: string;
    reportId: string;
    memberId: string;
    member: {
      id: string;
      fullName: string;
      email: string;
    };
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    approvedAt?: string | null;
    comments?: string | null;
    createdAt: string;
    updatedAt: string;
  }>;
  approvalType?: 'CHECK' | 'APPROVE' | 'BID_COMMITTEE' | 'ADMIN_VIEW';
  approvalId?: string;
}

export interface GenerateEvaluationReportDto {
  checkedById: string;
  approvedById: string;
}

export interface ApproveReportDto {
  comments?: string;
}

export interface AddBidCommitteeMembersDto {
  memberIds: string[];
}

export const evaluationReportsApi = {
  // Complete evaluation
  completeEvaluation: async (tenderId: string): Promise<{ message: string; stage: string }> => {
    const response = await api.post(`/tenders/${tenderId}/complete-evaluation`);
    return response.data;
  },

  // Generate evaluation report
  generateEvaluationReport: async (
    tenderId: string,
    data: GenerateEvaluationReportDto
  ): Promise<TenderEvaluationReport> => {
    const response = await api.post(`/tenders/${tenderId}/evaluation-report`, data);
    return response.data;
  },

  // Get evaluation report
  getEvaluationReport: async (tenderId: string): Promise<TenderEvaluationReport | null> => {
    try {
      const response = await api.get(`/tenders/${tenderId}/evaluation-report`);
      console.log('Evaluation report API response status:', response.status);
      console.log('Evaluation report API response data:', response.data);
      console.log('Evaluation report API response data type:', typeof response.data);
      console.log('Evaluation report API response data keys:', response.data ? Object.keys(response.data) : 'no data');
      
      if (!response.data || (typeof response.data === 'object' && Object.keys(response.data).length === 0)) {
        console.warn('Evaluation report response is empty or invalid');
        return null;
      }
      
      // Ensure reportData is an object, not a string
      if (response.data.reportData && typeof response.data.reportData === 'string') {
        try {
          response.data.reportData = JSON.parse(response.data.reportData);
        } catch (e) {
          console.error('Failed to parse reportData:', e);
        }
      }
      
      console.log('Returning evaluation report:', response.data.id);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching evaluation report:', error);
      console.error('Error response:', error.response?.data);
      // Return null if report doesn't exist (404)
      if (error.response?.status === 404) {
        console.log('Report not found (404), returning null');
        return null;
      }
      throw error;
    }
  },

  // Check report (procurement officer)
  checkReport: async (reportId: string, data: ApproveReportDto): Promise<TenderEvaluationReport> => {
    const response = await api.post(`/tenders/evaluation-reports/${reportId}/check`, data);
    return response.data;
  },

  // Reject check (procurement officer)
  rejectCheck: async (reportId: string, data: ApproveReportDto): Promise<TenderEvaluationReport> => {
    const response = await api.post(`/tenders/evaluation-reports/${reportId}/reject-check`, data);
    return response.data;
  },

  // Approve report (head of procurement)
  approveReport: async (reportId: string, data: ApproveReportDto): Promise<TenderEvaluationReport> => {
    const response = await api.post(`/tenders/evaluation-reports/${reportId}/approve`, data);
    return response.data;
  },

  // Reject approval (head of procurement)
  rejectApproval: async (reportId: string, data: ApproveReportDto): Promise<TenderEvaluationReport> => {
    const response = await api.post(`/tenders/evaluation-reports/${reportId}/reject-approval`, data);
    return response.data;
  },

  // Add bid committee members
  addBidCommitteeMembers: async (
    reportId: string,
    data: AddBidCommitteeMembersDto
  ): Promise<TenderEvaluationReport> => {
    const response = await api.post(`/tenders/evaluation-reports/${reportId}/bid-committee-members`, data);
    return response.data;
  },

  // Approve by bid committee member
  approveByBidCommittee: async (reportId: string, data: ApproveReportDto): Promise<TenderEvaluationReport> => {
    const response = await api.post(`/tenders/evaluation-reports/${reportId}/approve-bid-committee`, data);
    return response.data;
  },

  // Reject by bid committee member
  rejectByBidCommittee: async (reportId: string, data: ApproveReportDto): Promise<TenderEvaluationReport> => {
    const response = await api.post(`/tenders/evaluation-reports/${reportId}/reject-bid-committee`, data);
    return response.data;
  },

  // Get pending approvals
  getPendingReportApprovals: async (): Promise<TenderEvaluationReport[]> => {
    const response = await api.get('/tenders/evaluation-reports/pending-approvals');
    return response.data;
  },

  // Download report PDF
  downloadReportPDF: async (tenderId: string): Promise<Blob> => {
    const response = await api.get(`/tenders/${tenderId}/evaluation-report/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

